import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft, Loader2, Save, ShieldCheck, ShieldOff,
  AlertTriangle, Wallet, Building2, Tag as TagIcon,
  Eye, EyeOff, CheckCircle2, Settings, FileText, X,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/api/apiClient';
import { useOdooAuth } from '@/lib/OdooAuthContext';
import { cur } from '@/lib/currency';

/**
 * Per-rep settings editor.
 *
 * Loads the current profile and the various lookup lists (warehouses,
 * dealer categories, cash journals, screen keys) in parallel, then
 * lets the manager toggle every flag and save them all in one POST.
 */

const SECTIONS = [
  { key: 'general', label: 'عام', icon: Settings },
  { key: 'payments', label: 'الدفع', icon: Wallet },
  { key: 'scope', label: 'النطاق', icon: Building2 },
  { key: 'permissions', label: 'الصلاحيات', icon: ShieldCheck },
  { key: 'screens', label: 'الشاشات', icon: Eye },
];

export default function AdminRepProfile() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { odooSession } = useOdooAuth();

  const [profile, setProfile] = useState(null);
  const [lookups, setLookups] = useState({
    warehouses: [], dealer_categories: [], journals: [], screens: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('general');
  const [dirty, setDirty] = useState(false);

  // Guard: managers only.
  useEffect(() => {
    if (odooSession && odooSession.role !== 'manager') {
      navigate('/', { replace: true });
    }
  }, [odooSession, navigate]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [listRes, lookRes] = await Promise.all([
          api.get('/sellgo_profiles_list'),
          api.get('/sellgo_admin_lookups'),
        ]);
        if (listRes?.success) {
          const found = (listRes.data || []).find(p => String(p.id) === String(id));
          if (!found) {
            toast.error('الملف غير موجود');
            navigate('/admin/reps', { replace: true });
            return;
          }
          // Normalise array shapes for the form.
          setProfile({
            ...found,
            // m2m fields come as [{id,name}] — keep that shape.
            warehouse_ids: found.warehouse_ids || [],
            dealer_category_ids: found.dealer_category_ids || [],
            hidden_screens: found.hidden_screens || [],
            // Many2one — flatten to id for the picker.
            default_journal_id_val: Array.isArray(found.default_journal_id)
              ? found.default_journal_id[0] : (found.default_journal_id || false),
            supervisor_id_val: Array.isArray(found.supervisor_id)
              ? found.supervisor_id[0] : (found.supervisor_id || false),
          });
        }
        if (lookRes?.success) setLookups(lookRes.data || {});
      } catch (e) {
        toast.error(e?.message || 'فشل التحميل');
      }
      setLoading(false);
    };
    load();
  }, [id, navigate]);

  const set = (key, value) => {
    setProfile(p => ({ ...p, [key]: value }));
    setDirty(true);
  };

  const toggleScreen = (key) => {
    setProfile(p => {
      const has = (p.hidden_screens || []).includes(key);
      const next = has
        ? p.hidden_screens.filter(k => k !== key)
        : [...(p.hidden_screens || []), key];
      return { ...p, hidden_screens: next };
    });
    setDirty(true);
  };

  const toggleWarehouse = (w) => {
    setProfile(p => {
      const exists = (p.warehouse_ids || []).some(x => x.id === w.id);
      const next = exists
        ? p.warehouse_ids.filter(x => x.id !== w.id)
        : [...(p.warehouse_ids || []), w];
      return { ...p, warehouse_ids: next };
    });
    setDirty(true);
  };

  const toggleCategory = (c) => {
    setProfile(p => {
      const exists = (p.dealer_category_ids || []).some(x => x.id === c.id);
      const next = exists
        ? p.dealer_category_ids.filter(x => x.id !== c.id)
        : [...(p.dealer_category_ids || []), c];
      return { ...p, dealer_category_ids: next };
    });
    setDirty(true);
  };

  const save = async () => {
    if (!profile) return;
    setSaving(true);
    try {
      // Translate the UI's screen-keys list into the per-screen boolean
      // fields the backend now expects (hide_dashboard, hide_customers …).
      const hiddenSet = new Set(profile.hidden_screens || []);
      const hideFlags = {
        hide_dashboard:   hiddenSet.has('dashboard'),
        hide_customers:   hiddenSet.has('customers'),
        hide_products:    hiddenSet.has('products'),
        hide_orders:      hiddenSet.has('orders'),
        hide_collections: hiddenSet.has('collections'),
        hide_visits:      hiddenSet.has('visits'),
        hide_reports:     hiddenSet.has('reports'),
        hide_settings:    hiddenSet.has('settings'),
      };

      const payload = {
        id: profile.id,
        active: !!profile.active,
        order_confirm_limit: Number(profile.order_confirm_limit) || 0,
        gift_monthly_limit: Number(profile.gift_monthly_limit) || 0,
        require_manager_approval_above_limit:
          !!profile.require_manager_approval_above_limit,
        allow_cash: !!profile.allow_cash,
        allow_check: !!profile.allow_check,
        allow_bank_transfer: !!profile.allow_bank_transfer,
        allow_wallet: !!profile.allow_wallet,
        allow_advance_payment: !!profile.allow_advance_payment,
        allow_add_customer: !!profile.allow_add_customer,
        allow_add_product: !!profile.allow_add_product,
        allow_edit_price: !!profile.allow_edit_price,
        allow_create_order: !!profile.allow_create_order,
        allow_confirm_order: !!profile.allow_confirm_order,
        allow_cancel_order: !!profile.allow_cancel_order,
        allow_log_visit: !!profile.allow_log_visit,
        ...hideFlags,
        notes: profile.notes || '',
        default_journal_id: profile.default_journal_id_val || false,
        warehouse_ids: (profile.warehouse_ids || []).map(w => w.id),
        dealer_category_ids: (profile.dealer_category_ids || []).map(c => c.id),
      };
      const res = await api.post('/sellgo_profile_save', payload);
      if (res?.success) {
        toast.success('تم الحفظ بنجاح');
        setDirty(false);
      } else {
        toast.error(res?.error || 'فشل الحفظ');
      }
    } catch (e) {
      toast.error(e?.message || 'حدث خطأ');
    }
    setSaving(false);
  };

  if (loading || !profile) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const userName = profile.user_id?.[1] || profile.user_login || 'مندوب';

  return (
    <div className="flex flex-col min-h-full" dir="rtl">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background border-b border-border px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => {
            if (dirty && !confirm('فيه تغييرات غير محفوظة. خروج؟')) return;
            navigate('/admin/reps');
          }}
          className="w-9 h-9 rounded-xl bg-muted hover:bg-muted/70 flex items-center justify-center"
        >
          <ChevronLeft className="w-5 h-5 rotate-180" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="font-black text-foreground text-base truncate">
            {userName}
          </h1>
          <p className="text-xs text-muted-foreground truncate">
            {profile.user_email || profile.user_login || ''}
          </p>
        </div>
        <Button
          onClick={save}
          disabled={!dirty || saving}
          className="rounded-xl gap-2 bg-primary hover:bg-primary/90"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          حفظ
        </Button>
      </div>

      {/* Section tabs */}
      <div className="sticky top-[60px] z-[9] bg-background border-b border-border px-4 py-2.5 overflow-x-auto">
        <div className="flex gap-1.5 min-w-max">
          {SECTIONS.map(s => {
            const Icon = s.icon;
            const active = activeSection === s.key;
            return (
              <button
                key={s.key}
                onClick={() => setActiveSection(s.key)}
                className={`px-3.5 py-2 rounded-xl text-sm font-bold whitespace-nowrap transition-all flex items-center gap-1.5 ${
                  active
                    ? 'bg-primary text-white shadow-sm'
                    : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                }`}
              >
                <Icon className="w-4 h-4" />
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 pb-24 space-y-4">
        {activeSection === 'general' && (
          <GeneralSection profile={profile} set={set} />
        )}
        {activeSection === 'payments' && (
          <PaymentsSection profile={profile} set={set} journals={lookups.journals || []} />
        )}
        {activeSection === 'scope' && (
          <ScopeSection
            profile={profile}
            warehouses={lookups.warehouses || []}
            categories={lookups.dealer_categories || []}
            toggleWarehouse={toggleWarehouse}
            toggleCategory={toggleCategory}
          />
        )}
        {activeSection === 'permissions' && (
          <PermissionsSection profile={profile} set={set} />
        )}
        {activeSection === 'screens' && (
          <ScreensSection
            profile={profile}
            screens={lookups.screens || []}
            toggleScreen={toggleScreen}
          />
        )}
      </div>

      {/* Floating save bar for unsaved changes */}
      {dirty && (
        <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border p-3 flex items-center justify-between gap-3 shadow-lg z-20">
          <p className="text-sm text-amber-700 font-bold flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4" />
            تغييرات غير محفوظة
          </p>
          <Button
            onClick={save}
            disabled={saving}
            className="rounded-xl gap-2 bg-primary hover:bg-primary/90"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            احفظ التغييرات
          </Button>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
//   Section: General
// ────────────────────────────────────────────────────────────────────

function GeneralSection({ profile, set }) {
  return (
    <>
      <Card>
        <SectionHeader icon={Settings} title="الحالة">
          الملف موقوف يمنع المندوب من استخدام تطبيق SellGo.
        </SectionHeader>
        <ToggleRow
          label="ملف نشط"
          desc="عند الإيقاف، المندوب يقدر يدخل Odoo لكن مش هيقدر يستخدم SellGo."
          value={profile.active}
          onChange={v => set('active', v)}
        />
      </Card>

      <Card>
        <SectionHeader icon={FileText} title="سقف أمر البيع">
          أكبر مبلغ يقدر المندوب يأكّد عليه بدون اعتماد المشرف.
          صفر = بدون حد.
        </SectionHeader>
        <div>
          <label className="text-xs text-muted-foreground font-bold mb-1.5 block">
            السقف (جنيه)
          </label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={0}
              step={500}
              value={profile.order_confirm_limit || 0}
              onChange={e => set('order_confirm_limit', e.target.value)}
              className="h-11 bg-muted/30 rounded-xl text-base font-bold"
            />
            <span className="text-sm font-bold text-muted-foreground">{cur('ج.م')}</span>
          </div>
        </div>
        <div>
          <label className="text-xs text-muted-foreground font-bold mb-1.5 block">
            سقف الهدايا الشهري (جنيه)
          </label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={0}
              step={100}
              value={profile.gift_monthly_limit || 0}
              onChange={e => set('gift_monthly_limit', e.target.value)}
              className="h-11 bg-muted/30 rounded-xl text-base font-bold"
            />
            <span className="text-sm font-bold text-muted-foreground">{cur('ج.م')}</span>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">
            أقصى قيمة هدايا تُصرف من مخزن المندوب شهرياً. صفر = بدون حد.
          </p>
        </div>
        <ToggleRow
          label="يطلب اعتماد المشرف فوق السقف"
          desc="بدلاً من رفض الطلب، يبعت notification للمشرف."
          value={profile.require_manager_approval_above_limit}
          onChange={v => set('require_manager_approval_above_limit', v)}
        />
      </Card>

      <Card>
        <SectionHeader icon={FileText} title="ملاحظات">
          ملاحظات داخلية لا يراها المندوب نفسه.
        </SectionHeader>
        <Textarea
          value={profile.notes || ''}
          onChange={e => set('notes', e.target.value)}
          placeholder="ملاحظات..."
          rows={3}
          className="bg-muted/30 rounded-xl"
        />
      </Card>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────
//   Section: Payments
// ────────────────────────────────────────────────────────────────────

function PaymentsSection({ profile, set, journals }) {
  return (
    <>
      <Card>
        <SectionHeader icon={Wallet} title="طرق الدفع المسموحة">
          المندوب يقدر يسجّل تحصيلات بهذه الطرق فقط.
        </SectionHeader>
        <ToggleRow label="💵 نقدي" value={profile.allow_cash} onChange={v => set('allow_cash', v)} />
        <ToggleRow label="📝 شيك" value={profile.allow_check} onChange={v => set('allow_check', v)} />
        <ToggleRow label="🏦 تحويل بنكي" value={profile.allow_bank_transfer} onChange={v => set('allow_bank_transfer', v)} />
        <ToggleRow label="📱 محفظة إلكترونية" value={profile.allow_wallet} onChange={v => set('allow_wallet', v)} />
        <div className="border-t border-border my-2"/>
        <ToggleRow
          label="السماح بالدفعة المقدمة"
          desc="تحصيل بدون فاتورة (رصيد دائن)."
          value={profile.allow_advance_payment}
          onChange={v => set('allow_advance_payment', v)}
        />
      </Card>

      <Card>
        <SectionHeader icon={Building2} title="الجورنال الافتراضي">
          الـ Journal اللي تروح له التحصيلات النقدية تلقائياً.
        </SectionHeader>
        <select
          value={profile.default_journal_id_val || ''}
          onChange={e => set('default_journal_id_val', e.target.value ? Number(e.target.value) : false)}
          className="w-full h-11 px-3 bg-muted/30 rounded-xl border border-border focus:outline-none focus:ring-2 focus:ring-primary/30"
        >
          <option value="">— بدون اختيار —</option>
          {journals.map(j => (
            <option key={j.id} value={j.id}>
              {j.name} ({j.type})
            </option>
          ))}
        </select>
      </Card>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────
//   Section: Scope (warehouses + dealer categories)
// ────────────────────────────────────────────────────────────────────

function ScopeSection({ profile, warehouses, categories, toggleWarehouse, toggleCategory }) {
  const selectedWh = useMemo(
    () => new Set((profile.warehouse_ids || []).map(w => w.id)),
    [profile.warehouse_ids]
  );
  const selectedCat = useMemo(
    () => new Set((profile.dealer_category_ids || []).map(c => c.id)),
    [profile.dealer_category_ids]
  );

  return (
    <>
      <Card>
        <SectionHeader icon={Building2} title="المخازن المسموحة">
          المندوب هيشوف ويتعامل مع منتجات هذه المخازن فقط.
        </SectionHeader>
        <div className="flex flex-wrap gap-2">
          {warehouses.length === 0 ? (
            <p className="text-xs text-muted-foreground">لا يوجد مخازن في النظام</p>
          ) : (
            warehouses.map(w => {
              const on = selectedWh.has(w.id);
              return (
                <button
                  key={w.id}
                  onClick={() => toggleWarehouse(w)}
                  className={`px-3 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-1.5 ${
                    on
                      ? 'bg-primary text-white'
                      : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {on && <CheckCircle2 className="w-3.5 h-3.5" />}
                  {w.name}
                </button>
              );
            })
          )}
        </div>
      </Card>

      <Card>
        <SectionHeader icon={TagIcon} title="فئات الـ Dealer">
          المندوب هيشوف فقط العملاء والمنتجات اللي عليهم هذه الفئات.
        </SectionHeader>
        <div className="flex flex-wrap gap-2">
          {categories.length === 0 ? (
            <p className="text-xs text-muted-foreground">لا يوجد فئات في النظام</p>
          ) : (
            categories.map(c => {
              const on = selectedCat.has(c.id);
              return (
                <button
                  key={c.id}
                  onClick={() => toggleCategory(c)}
                  className={`px-3 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-1.5 ${
                    on
                      ? 'bg-emerald-600 text-white'
                      : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {on && <CheckCircle2 className="w-3.5 h-3.5" />}
                  {c.name}
                </button>
              );
            })
          )}
        </div>
      </Card>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────
//   Section: Permissions
// ────────────────────────────────────────────────────────────────────

function PermissionsSection({ profile, set }) {
  return (
    <>
      <Card>
        <SectionHeader icon={ShieldCheck} title="إضافة بيانات">
          هل المندوب يقدر يضيف عميل أو منتج جديد؟
        </SectionHeader>
        <ToggleRow
          label="يضيف عميل جديد"
          value={profile.allow_add_customer}
          onChange={v => set('allow_add_customer', v)}
        />
        <ToggleRow
          label="يضيف منتج جديد"
          value={profile.allow_add_product}
          onChange={v => set('allow_add_product', v)}
        />
        <ToggleRow
          label="يعدّل سعر الوحدة في الطلب"
          desc="إذا متفعّلش، الأسعار من قائمة الأسعار فقط."
          value={profile.allow_edit_price}
          onChange={v => set('allow_edit_price', v)}
        />
      </Card>

      <Card>
        <SectionHeader icon={FileText} title="أوامر البيع">
          صلاحياته على الطلبات.
        </SectionHeader>
        <ToggleRow
          label="ينشئ أمر بيع"
          value={profile.allow_create_order}
          onChange={v => set('allow_create_order', v)}
        />
        <ToggleRow
          label="يأكّد أمر بيع"
          desc="مع مراعاة سقف البيع."
          value={profile.allow_confirm_order}
          onChange={v => set('allow_confirm_order', v)}
        />
        <ToggleRow
          label="يلغي أمر بيع"
          value={profile.allow_cancel_order}
          onChange={v => set('allow_cancel_order', v)}
        />
      </Card>

      <Card>
        <SectionHeader icon={FileText} title="أخرى">
          صلاحيات إضافية.
        </SectionHeader>
        <ToggleRow
          label="يسجّل زيارة"
          value={profile.allow_log_visit}
          onChange={v => set('allow_log_visit', v)}
        />
      </Card>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────
//   Section: Screens
// ────────────────────────────────────────────────────────────────────

function ScreensSection({ profile, screens, toggleScreen }) {
  const hidden = new Set(profile.hidden_screens || []);
  return (
    <Card>
      <SectionHeader icon={EyeOff} title="إخفاء الشاشات">
        الشاشات اللي مفيش عليها علامة تظهر للمندوب. علّم اللي عاوز تخفيها.
      </SectionHeader>
      <div className="grid grid-cols-2 gap-2">
        {screens.map(s => {
          const isHidden = hidden.has(s.key);
          return (
            <button
              key={s.key}
              onClick={() => toggleScreen(s.key)}
              className={`p-3 rounded-xl border-2 transition-all flex items-center gap-2 ${
                isHidden
                  ? 'border-red-200 bg-red-50'
                  : 'border-emerald-200 bg-emerald-50'
              }`}
            >
              {isHidden ? <EyeOff className="w-4 h-4 text-red-600" /> : <Eye className="w-4 h-4 text-emerald-600" />}
              <span className={`text-sm font-bold ${isHidden ? 'text-red-700 line-through' : 'text-emerald-700'}`}>
                {s.label}
              </span>
            </button>
          );
        })}
      </div>
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────────
//   Shared sub-components
// ────────────────────────────────────────────────────────────────────

function Card({ children }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-4 space-y-3">
      {children}
    </div>
  );
}

function SectionHeader({ icon: Icon, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon className="w-4 h-4 text-primary" />}
        <h3 className="font-bold text-foreground text-base">{title}</h3>
      </div>
      {children && (
        <p className="text-xs text-muted-foreground leading-relaxed">{children}</p>
      )}
    </div>
  );
}

function ToggleRow({ label, desc, value, onChange }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-foreground">{label}</p>
        {desc && <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>}
      </div>
      <button
        onClick={() => onChange(!value)}
        className={`w-12 h-7 rounded-full relative flex-shrink-0 transition-colors ${
          value ? 'bg-primary' : 'bg-muted-foreground/30'
        }`}
      >
        <span
          className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-all ${
            value ? 'right-0.5' : 'right-[calc(100%-1.625rem)]'
          }`}
        />
      </button>
    </div>
  );
}
